import { useState, useEffect, useCallback } from "react";
import { supabase } from "../providers/AuthProvider";

export function useUnreadCount(ticketId: string | null) {
  const [unreadCount, setUnreadCount] = useState(0);

  const updateUnreadCount = useCallback(async () => {
    if (!ticketId) {
      setUnreadCount(0);
      return;
    }

    try {
      const { count, error } = await supabase
        .from("support_messages")
        .select("*", { count: "exact", head: true })
        .eq("ticket_id", ticketId)
        .eq("is_from_admin", false)
        .is("read_at", null);

      if (error) throw error;
      setUnreadCount(count || 0);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  }, [ticketId]);

  useEffect(() => {
    updateUnreadCount();
  }, [updateUnreadCount]);

  return {
    unreadCount,
    updateUnreadCount,
  };
}

export function useTotalUnreadCount() {
  const [totalUnread, setTotalUnread] = useState(0);

  useEffect(() => {
    const fetchTotalUnread = async () => {
      try {
        // Verificar se a tabela existe antes de fazer a query
        const { data: tickets, error: ticketsError } = await supabase
          .from("support_tickets")
          .select("id")
          .in("status", ["open", "in_progress"]);

        if (ticketsError) {
          // Se a tabela não existe ou há erro de permissão, retornar 0 silenciosamente
          if (
            ticketsError.code === "PGRST116" ||
            ticketsError.message?.includes("schema cache") ||
            ticketsError.message?.includes("permission denied") ||
 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
            (ticketsError as any).status === 403 ||
            ticketsError.code === "42501"
          ) {
            console.warn(
              "Tabela support_tickets não encontrada ou sem permissão, retornando 0",
            );
            setTotalUnread(0);
            return;
          }
          throw ticketsError;
        }
        if (!tickets || tickets.length === 0) {
          setTotalUnread(0);
          return;
        }

        const ticketIds = tickets.map((t) => t.id);

        const { count, error } = await supabase
          .from("support_messages")
          .select("*", { count: "exact", head: true })
          .in("ticket_id", ticketIds)
          .eq("is_from_admin", false)
          .is("read_at", null);

        if (error) throw error;
 
        setTotalUnread(count || 0);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        // Tratar erros de permissão (403) ou tabela não encontrada silenciosamente
        if (
          error?.code === "PGRST116" ||
          error?.message?.includes("schema cache") ||
          error?.message?.includes("permission denied") ||
          error?.status === 403 ||
          error?.code === "42501"
        ) {
          console.warn(
            "Erro de permissão ou tabela não encontrada, retornando 0:",
            error?.message || error?.code,
          );
          setTotalUnread(0);
          return;
        }
        console.error(
          "Error fetching total unread count:",
          error?.message || error,
        );
        setTotalUnread(0);
      }
    };

    fetchTotalUnread();
    const interval = setInterval(fetchTotalUnread, 30000);

    const channel = supabase
      .channel("admin_unread_count_updates")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "support_messages",
          filter: "is_from_admin=eq.false",
        },
        () => fetchTotalUnread(),
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "support_messages" },
        () => fetchTotalUnread(),
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, []);

  return totalUnread;
}
